import React, { useState, useEffect } from 'react';
import { Loader2, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { getRentals } from '../api';

const Rentals = () => {
  const { t, i18n } = useTranslation();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRentals = async () => {
      try {
        const data = await getRentals();
        setRentals(data);
      } catch (err) {
        setError(t('rentals.error'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRentals();
  }, [t, i18n.language]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="rentals" className="py-24 bg-gradient-to-b from-white to-pink-50">
        <div className="container mx-auto px-4 flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="rentals" className="py-24 bg-gradient-to-b from-white to-pink-50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="rentals" className="py-24 bg-gradient-to-b from-white to-pink-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-fuchsia-600 via-pink-600 to-orange-500 bg-clip-text text-transparent">
              {t('rentals.title')}
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            {t('rentals.subtitle')}
          </p>
        </div>

        {/* Rentals Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rentals.map((rental, index) => (
            <Card
              key={rental.id}
              className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={rental.image.startsWith('/api/') 
                    ? `${process.env.REACT_APP_BACKEND_URL}${rental.image}` 
                    : rental.image}
                  alt={i18n.language === 'en' ? (rental.title_en || rental.title) : rental.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white text-xl font-bold">
                      {i18n.language === 'en' ? (rental.title_en || rental.title) : rental.title}
                    </h3>
                  </div>
                </div>
              </div>

              <CardHeader>
                <CardDescription className="text-gray-600 text-base leading-relaxed">
                  {i18n.language === 'en' ? (rental.description_en || rental.description) : rental.description}
                </CardDescription>
              </CardHeader>

              <CardFooter className="flex items-center justify-between">
                <span className="text-xl font-bold text-orange-600">
                  {rental.price}{t('rentals.perDay')}
                </span>
                <Button
                  onClick={scrollToContact}
                  variant="ghost"
                  className="text-pink-600 hover:text-pink-700 group/btn font-semibold"
                >
                  {t('rentals.reserve')}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-orange-100 via-pink-100 to-fuchsia-100 rounded-2xl p-8 max-w-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('rentals.heliumTitle')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('rentals.heliumDescription')}
            </p>
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-gradient-to-r from-orange-500 via-pink-600 to-fuchsia-600 hover:from-orange-600 hover:via-pink-700 hover:to-fuchsia-700 text-white px-8 py-6 rounded-full shadow-xl"
            >
              {t('rentals.contactButton')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Rentals;
